import React, { memo, useEffect, useMemo, useState } from 'react'
import { IStringInputSchema } from '../../../../../types'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import {
    selectRuleConfigBit,
    selectRuleIsChecked,
} from '../../../../../../../store/selectors/ruleCardSelectors'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { inputValidationCheck } from '../../../../../../../validators/inputValidators/inputValidators'
import { useFormValidation } from '../../../../../../../hooks/useFormValidation'
import { getLabel } from '../../../../../../../utils/getLabel'
import { stringInputErrors } from '../../../../../../../utils/validationErrors/stringInputErrors'
import { initInputData } from '../../../../../../../utils/InitInputData'
import { inputDataDispatcher } from '../../../../../../../utils/InputDataDispatcher'
import { buildControlsExt, textInput } from '../../../../../../inputs/controls'

interface Props {
    dataPath: string
    pathWithElementID: string | undefined
    dataSchema: IStringInputSchema
    name: string
    wrapper?: string
}

export const RuleTextarea = memo(function RuleTextarea({
    dataPath,
    dataSchema,
    name,
    wrapper,
    pathWithElementID,
}: Props) {
    const dispatch = useAppDispatch()
    const pathSplit = useMemo(
        () => (dataPath?.length ? dataPath.split(PATH_JOINER) : []),
        [dataPath]
    )
    const path = useMemo(() => [...pathSplit, name], [pathSplit, name])
    const configBit = useAppSelector(selectRuleConfigBit(pathSplit))
    const [checkPath, setCheckPath] = useState<string[]>([])

    const isChecked = useAppSelector(selectRuleIsChecked)

    const {
        is_required: isRequired,
        name: inputLabel,
        type,
        description: tooltipText,
        is_email: isEmail,
        default: defaultValue,
    } = dataSchema

    const validationCheck = inputValidationCheck(type, dataSchema)
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit || {}, { [name]: validationCheck })

    const label: string = getLabel(name, inputLabel)
    const className: string = !wrapper ? 'w-50' : ''
    const errorKey: string = pathWithElementID
        ? [...pathWithElementID.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const isUndefined: boolean = typeof configBit?.[name] === 'undefined'
    const isDataEmpty: number = Object.keys(configBit || {}).length
    const isEmpty: boolean = !configBit || !isDataEmpty || isUndefined
    const errMsg = stringInputErrors(inputState?.[name], dataSchema)

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            initInputData(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    isRequired,
                    errorKey,
                    data: configBit,
                    setInputState,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (isChecked && dataPath.length) {
            isValid()
        }
    }, [isChecked, dataPath.length]) // eslint-disable-line

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit, dataPath.length]) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            inputDataDispatcher(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    data: configBit,
                    inputState,
                    validationCheck,
                    errorKey,
                    isUndefined,
                    checkPath,
                    type,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, [touched[name], isChecked]) // eslint-disable-line

    const handleOnBlur = () => {
        inputDataDispatcher(
            {
                isEmpty,
                name,
                path,
                dataSchema,
                data: configBit,
                inputState,
                validationCheck,
                errorKey,
                isUndefined,
                checkPath,
                type,
                isRuleCard: true,
            },
            dispatch
        )
        setTouched((prev: any) => ({ ...prev, [name]: true }))
    }

    const controlDescriptors = [
        textInput({
            name,
            tooltipText,
            label,
            className,
            isColumn: true,
            errMsg,
            isRequired,
            isEmail,
            placeholder: defaultValue,
            onBlur: handleOnBlur,
        }),
    ]

    const TextArea = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    return <div className={`${!wrapper ? 'w-100' : ''}`}>{TextArea}</div>
})
